import React from 'react';
import Button from './Button';
import Icon from '../AppIcon';

/**
 * Confirmation dialog component for confirming user actions
 * @param {Object} props
 * @param {string} props.title - Dialog title
 * @param {string} props.message - Dialog message
 * @param {string} props.confirmLabel - Label for confirm button
 * @param {string} props.cancelLabel - Label for cancel button
 * @param {Function} props.onConfirm - Function to call on confirm
 * @param {Function} props.onCancel - Function to call on cancel
 * @param {boolean} props.isLoading - Whether the dialog is in loading state
 * @param {string} props.type - Dialog type (info, warning, danger, success)
 */
const ConfirmationDialog = ({
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  type = 'info'
}) => {
  const getIconName = () => {
    switch (type) {
      case 'danger': return 'AlertTriangle';
      case 'warning': return 'AlertCircle';
      case 'success': return 'CheckCircle';
      case 'info':
      default: return 'Info';
    }
  };
  
  const getIconColor = () => {
    switch (type) {
      case 'danger': return 'text-error';
      case 'warning': return 'text-warning';
      case 'success': return 'text-success';
      case 'info':
      default: return 'text-info';
    }
  };
  
  const getButtonVariant = () => {
    switch (type) {
      case 'danger': return 'danger';
      case 'warning': return 'warning';
      case 'success': return 'success';
      case 'info':
      default: return 'primary';
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full animate-fade-in">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-xl font-semibold text-text-primary">{title}</h3>
          </div>
          
          <p className="text-text-secondary mb-6">{message}</p>
          
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={isLoading}
            >
              {cancelLabel}
            </Button>
            <Button
              variant={getButtonVariant()}
              onClick={onConfirm}
              isLoading={isLoading}
              className={`
                ${type === 'danger' && 'bg-error hover:bg-error-600 text-white'}
                ${type === 'warning' && 'bg-warning hover:bg-warning-600 text-white'}
                ${type === 'success' && 'bg-success hover:bg-success-600 text-white'}
                ${type === 'info' && 'bg-primary hover:bg-primary-600 text-white'}
              `}
            >
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationDialog;
